import React from 'react';
import { Check, Zap, Star, Shield } from 'lucide-react';
import { Button } from './ui/Button';
interface BundleCardProps {
  quantity: number;
  price: string;
  originalPrice?: string;
  savings?: number;
  isBestValue?: boolean;
  bonuses?: string[];
  onSelect: () => void;
  productImage?: string;
  perDayOverride?: string;
  pricePerBox?: string;
  patches?: number;
}
// Helper to compute per-day price
function getPerDay(price: string): string {
  const num = parseFloat(price.replace(/[^0-9,.]/g, '').replace(',', '.'));
  const days = 270;
  return (num / days).toFixed(2).replace('.', ',');
}
export function BundleCard({
  quantity,
  price,
  originalPrice,
  savings,
  isBestValue,
  bonuses = [],
  onSelect,
  productImage = 'https://cdn.magicpatterns.com/uploads/q5pbc7UPXB7yZsJKNiRqwd/LymphoriaX_(6).png',
  perDayOverride,
  pricePerBox,
  patches
}: BundleCardProps) {
  const perDay = perDayOverride ?? getPerDay(price);
  return <div className={`
      relative flex flex-col rounded-2xl border-2 p-6 transition-all
      ${isBestValue ? 'border-[#D4AF37] shadow-2xl shadow-[#D4AF37]/20 scale-105 z-10 bg-gradient-to-b from-white to-[#FDFBF7]' : 'border-gray-200 shadow-sm hover:border-[#D4AF37]/50 bg-white'}
    `} data-id="element-214">
      {isBestValue && <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#D4AF37] to-[#B5952F] text-white px-5 py-1.5 rounded-full text-sm font-black uppercase tracking-wide whitespace-nowrap shadow-lg" data-id="element-215">
          🏆 Más Popular — Mejor Resultado
        </div>}

      {savings && savings >= 70 && !isBestValue && <div className="absolute -top-3 right-4 bg-red-600 text-white px-3 py-1 rounded-full text-xs font-black uppercase shadow" data-id="element-216">
          -{savings}% OFF
        </div>}

      {/* Product Image Stack */}
      <div className="relative h-32 mb-4 flex items-center justify-center" data-id="element-217">
        {quantity === 1 ? <img src={productImage} alt="StingFit Pack" className="h-full w-auto object-contain" data-id="element-218" /> : quantity === 2 ? <div className="relative h-full w-full flex items-center justify-center" data-id="element-219">
            <img src={productImage} alt="StingFit Pack" className="h-full w-auto object-contain absolute left-4 -rotate-6 opacity-70" data-id="element-220" />
            <img src={productImage} alt="StingFit Pack" className="h-full w-auto object-contain absolute right-4 rotate-6" data-id="element-221" />
          </div> : quantity === 3 ? <div className="relative h-full w-full flex items-center justify-center" data-id="element-222">
            <img src={productImage} alt="StingFit Pack" className="h-full w-auto object-contain absolute left-2 -rotate-12 opacity-50" data-id="element-223" />
            <img src={productImage} alt="StingFit Pack" className="h-full w-auto object-contain relative z-10" data-id="element-224" />
            <img src={productImage} alt="StingFit Pack" className="h-full w-auto object-contain absolute right-2 rotate-12 opacity-50" data-id="element-225" />
          </div> : <div className="relative h-full w-full flex items-center justify-center" data-id="element-226">
            <div className="grid grid-cols-3 grid-rows-2 gap-0.5 h-full w-full" data-id="element-227">
              {[...Array(6)].map((_, i) => <img key={i} src={productImage} alt="StingFit Pack" className="h-full w-full object-contain opacity-70" data-id="element-228" />)}
            </div>
          </div>}
      </div>

      <div className="text-center mb-5 pt-2" data-id="element-229">
        <h3 className="text-lg font-black text-gray-900 mb-0.5 uppercase tracking-wide" data-id="element-230">
          {quantity} {quantity === 1 ? 'StingFit' : 'StingFit'}
        </h3>
        {savings && <div className="inline-block bg-green-100 text-green-800 text-xs font-black px-3 py-1 rounded-full mb-3 uppercase tracking-wide" data-id="element-231">
            Ahorras {savings}% — {originalPrice}
          </div>}
        {originalPrice && <p className="text-xs text-gray-400 line-through mb-1" data-id="element-232">
            {originalPrice}
          </p>}
        <div className="flex items-end justify-center gap-2 mb-1" data-id="element-233">
          <span className="text-4xl font-black text-[#2D5016]" data-id="element-234">{price}</span>
        </div>
        {pricePerBox && <p className="text-xs text-gray-500 font-semibold mb-1" data-id="element-235">
            {pricePerBox}/caja
          </p>}
        <p className="text-xs text-gray-400 font-medium" data-id="element-236">
          Solo <span className="font-black text-[#D4AF37]" data-id="element-237">{perDay} €/día</span>{' '}
          — menos que un café ☕
        </p>
      </div>

      <div className="flex-grow space-y-2.5 mb-5" data-id="element-238">
        <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-239">
          <Check className="w-4 h-4 text-[#D4AF37] flex-shrink-0" data-id="element-240" />
          <span className="font-medium" data-id="element-241">Envío Gratis Hoy</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-242">
          <Check className="w-4 h-4 text-[#D4AF37] flex-shrink-0" data-id="element-243" />
          <span className="font-medium" data-id="element-244">Garantía de 90 Días</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-245">
          <Shield className="w-4 h-4 text-[#D4AF37] flex-shrink-0" data-id="element-246" />
          <span className="font-medium" data-id="element-247">Pago 100% Seguro</span>
        </div>
        {isBestValue && <div className="flex items-center gap-2 text-sm text-[#2D5016] font-bold bg-green-50 px-3 py-2 rounded-lg" data-id="element-248">
            <Star className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37] flex-shrink-0" data-id="element-249" />
            <span data-id="element-250">Recomendado por el 78% de clientas</span>
          </div>}
        {bonuses.map((bonus, idx) => <div key={idx} className="flex items-center gap-2 text-sm text-gray-700 font-medium" data-id="element-251">
            <Check className="w-4 h-4 text-[#D4AF37] flex-shrink-0" data-id="element-252" />
            <span data-id="element-253">{bonus}</span>
          </div>)}
      </div>

      <div className="space-y-2" data-id="element-254">
        <Button variant={isBestValue ? 'primary' : 'outline'} fullWidth onClick={onSelect} className={`${isBestValue ? 'group shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all text-base' : ''} font-black`} data-id="element-255">
          {isBestValue ? <>
              <Zap className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform" data-id="element-256" />
              EMPEZAR MI TRANSFORMACIÓN
            </> : 'PEDIR AHORA'}
        </Button>

        <p className="text-xs text-center text-gray-400 font-medium" data-id="element-257">
          Compra única · Sin suscripción
        </p>
      </div>
    </div>;
}